import LookupRecord from '../Records/LookupRecord';
import type {
  AvatarIdIdentifier,
  AvatarUniquePropertyIdentifier,
} from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc';
import {
  AvatarIdIdentifierTypename,
  AvatarUniquePropertyIdentifierTypename,
} from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc';
import { LOOKUP_TYPE_TO_OBJECT_TYPE_ID } from '../Constants';

interface LookupByIdentifier {
  lookup: LookupRecord;
  identifier: AvatarIdIdentifier | AvatarUniquePropertyIdentifier;
  responseKey: string;
}

interface LookupByHubSpotUserEmail {
  lookup: LookupRecord;
  email: string;
}

export interface GroupedLookups {
  identifiers: LookupByIdentifier[];
  hubSpotUserEmails: LookupByHubSpotUserEmail[];
  unclassified: LookupRecord[];
}

const BY_UNIQUE_PROPERTY_CONFIG: Record<
  string,
  { objectTypeId: string; propertyName: string }
> = {
  contact: { objectTypeId: '0-1', propertyName: 'email' },
  email: { objectTypeId: '0-1', propertyName: 'email' },
  enrichmentEmail: { objectTypeId: '0-1', propertyName: 'email' },
  company: { objectTypeId: '0-2', propertyName: 'domain' },
  domain: { objectTypeId: '0-2', propertyName: 'domain' },
  enrichmentDomain: { objectTypeId: '0-2', propertyName: 'domain' },
};

const HUBSPOT_USER_EMAIL_TYPES = new Set([
  'hubSpotUserEmail',
  'hubSpotUserEmails',
  'ai-agent',
]);

const SECONDARY_IDENTIFIER_PROPERTY_NAMES: Record<string, string> = {
  contact: 'email',
  company: 'domain',
};

export function buildByIdResponseKey(identifier: AvatarIdIdentifier): string {
  const { objectTypeId, objectId } = identifier.objectTypeAndId;
  return `${objectTypeId}:${objectId}`;
}

export function buildByUniquePropertyResponseKey(
  identifier: AvatarUniquePropertyIdentifier
): string {
  const { objectTypeId, knownPropertyValue } = identifier;
  return `${objectTypeId}:${knownPropertyValue.propertyName}:${knownPropertyValue.propertyValue}`;
}

function tryClassifyAsHubSpotUserEmail(
  lookup: LookupRecord
): LookupByHubSpotUserEmail | undefined {
  const type = lookup.type;
  if (!type || !HUBSPOT_USER_EMAIL_TYPES.has(type)) return undefined;

  const email = lookup.primaryIdentifier;
  if (typeof email !== 'string') return undefined;

  return { lookup, email };
}

function tryClassifyByUniqueProperty(
  lookup: LookupRecord
): LookupByIdentifier | undefined {
  const type = lookup.type;
  if (!type) return undefined;

  const config = BY_UNIQUE_PROPERTY_CONFIG[type];
  if (!config) return undefined;

  const propertyValue = lookup.primaryIdentifier;
  if (typeof propertyValue !== 'string') return undefined;

  const identifier: AvatarUniquePropertyIdentifier = {
    identifierType: 'uniqueProperty',
    objectTypeId: config.objectTypeId,
    knownPropertyValue: {
      propertyName: config.propertyName,
      propertyValue,
    },
    __typename: AvatarUniquePropertyIdentifierTypename,
  };

  return {
    lookup,
    identifier,
    responseKey: buildByUniquePropertyResponseKey(identifier),
  };
}

function tryClassifyById(lookup: LookupRecord): LookupByIdentifier | undefined {
  const type = lookup.type;
  if (!type) return undefined;

  const objectTypeId = LOOKUP_TYPE_TO_OBJECT_TYPE_ID[type];
  if (!objectTypeId) return undefined;

  const objectId = lookup.primaryIdentifier;
  if (typeof objectId !== 'number') return undefined;

  const identifier: AvatarIdIdentifier = {
    identifierType: 'objectId',
    objectTypeAndId: { objectTypeId, objectId },
    __typename: AvatarIdIdentifierTypename,
  };

  if (lookup.fileManagerKey) {
    identifier.fileManagerKey = lookup.fileManagerKey;
  }

  const secondaryPropertyName = SECONDARY_IDENTIFIER_PROPERTY_NAMES[type];
  if (secondaryPropertyName && lookup.secondaryIdentifier) {
    identifier.knownPropertyValue = {
      propertyName: secondaryPropertyName,
      propertyValue: String(lookup.secondaryIdentifier),
    };
  }

  return {
    lookup,
    identifier,
    responseKey: buildByIdResponseKey(identifier),
  };
}

export function classifyLookups(lookups: Set<LookupRecord>): GroupedLookups {
  return Array.from(lookups).reduce<GroupedLookups>(
    (groups, lookup) => {
      const hsUserEmail = tryClassifyAsHubSpotUserEmail(lookup);
      if (hsUserEmail) {
        groups.hubSpotUserEmails.push(hsUserEmail);
        return groups;
      }

      const uniqueProp = tryClassifyByUniqueProperty(lookup);
      if (uniqueProp) {
        groups.identifiers.push(uniqueProp);
        return groups;
      }

      const byId = tryClassifyById(lookup);
      if (byId) {
        groups.identifiers.push(byId);
      } else {
        groups.unclassified.push(lookup);
      }

      return groups;
    },
    { identifiers: [], hubSpotUserEmails: [], unclassified: [] }
  );
}
